import { Injectable } from '@angular/core';
import {
  HttpInterceptor,
  HttpRequest,
  HttpHandler,
  HttpEvent,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Router } from '@angular/router';
import { ToastService } from '../services/toast.service';

/**
 * ErrorInterceptor — handles non-auth HTTP errors.
 *
 * 401 Unauthorized is intentionally NOT handled here.
 * It is fully managed by TokenRefreshInterceptor which runs before this
 * interceptor in the chain and either refreshes the token + retries,
 * or clears the session and redirects to /login.
 */
@Injectable()
export class ErrorInterceptor implements HttpInterceptor {

  constructor(private router: Router, private toast: ToastService) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        switch (error.status) {
          case 401:
            // Handled upstream by TokenRefreshInterceptor.
            // If a 401 reaches here the refresh also failed and the user
            // has already been redirected to /login — nothing to do.
            break;

          case 403:
            this.toast.error('You do not have permission to perform this action.');
            this.router.navigate(['/unauthorized']);
            break;

          case 0:
            this.toast.error('Unable to connect to the server. Please check your connection.');
            break;

          default:
            // 400, 404, 409, 422, 500, etc.
            // Skip toasting on auth endpoints — callers handle those themselves.
            if (!this.isAuthUrl(req.url)) {
              const msg =
                error.error?.message || error.message || 'An unexpected error occurred.';
              this.toast.error(msg);
            }
        }
        return throwError(() => error);
      })
    );
  }

  private isAuthUrl(url: string): boolean {
    return url.includes('/auth/');
  }
}
